/**
 * Questions and plans a coding tab is waiting on (PWA PH-02).
 *
 * The gateway parks a Claude turn on AskUserQuestion or ExitPlanMode, keeps a
 * durable record, and pushes `prompt` / `prompt_resolved` over the WS. The
 * cards read this store; answering posts to the gateway, which continues the
 * same Claude turn as a new chat turn.
 */
import { signal } from '@preact/signals';
import { api, ApiError, type ChatPrompt, type ChatPromptAnswer } from '../../core/api/client';

export const chatPrompts = signal<Record<number, ChatPrompt[]>>({});
/** Whether the chat's gateway relays prompts (drives the "Plan first" switch). */
export const promptRelay = signal<Record<number, boolean>>({});
/** Last answer error per prompt id (cleared on retry). */
export const promptErrors = signal<Record<string, string>>({});

// WS events seen per chat, and prompts resolved locally, so a slow GET cannot
// resurrect a resolved card or drop one that arrived while it was in flight.
const eventSeq = new Map<number, number>();
const resolvedIds = new Set<string>();

function bump(chatId: number) {
  eventSeq.set(chatId, (eventSeq.get(chatId) ?? 0) + 1);
}

function setChat(chatId: number, prompts: ChatPrompt[]) {
  chatPrompts.value = { ...chatPrompts.value, [chatId]: prompts };
}

function removePrompt(chatId: number, promptId: string) {
  resolvedIds.add(promptId);
  const current = chatPrompts.value[chatId] ?? [];
  if (current.some((prompt) => prompt.id === promptId)) {
    setChat(chatId, current.filter((prompt) => prompt.id !== promptId));
  }
  if (promptId in promptErrors.value) {
    const { [promptId]: _dropped, ...rest } = promptErrors.value;
    promptErrors.value = rest;
  }
}

function setError(promptId: string, message: string) {
  promptErrors.value = { ...promptErrors.value, [promptId]: message };
}

export async function loadChatPrompts(chatId: number): Promise<void> {
  const seqAtStart = eventSeq.get(chatId) ?? 0;
  try {
    const response = await api.getChatPrompts(chatId);
    let prompts = Array.isArray(response?.prompts)
      ? response.prompts.filter(
        (prompt) => prompt && prompt.status === 'pending' && !resolvedIds.has(prompt.id),
      )
      : [];
    if ((eventSeq.get(chatId) ?? 0) !== seqAtStart) {
      // WS events landed while the request was in flight: keep what they added.
      const known = new Set(prompts.map((prompt) => prompt.id));
      const live = (chatPrompts.value[chatId] ?? []).filter(
        (prompt) => !known.has(prompt.id) && !resolvedIds.has(prompt.id),
      );
      prompts = [...prompts, ...live];
    }
    setChat(chatId, prompts);
    promptRelay.value = { ...promptRelay.value, [chatId]: response?.relay === true };
  } catch {
    /* Cards are an overlay: a failed refresh keeps what the WS already delivered. */
  }
}

export async function answerPrompt(
  chatId: number,
  promptId: string,
  answer: ChatPromptAnswer,
): Promise<boolean> {
  try {
    await api.answerChatPrompt(chatId, promptId, answer);
    removePrompt(chatId, promptId);
    return true;
  } catch (error) {
    if (error instanceof ApiError && (error.status === 409 || error.status === 404)) {
      removePrompt(chatId, promptId); // already answered elsewhere, or gone
      return false;
    }
    setError(promptId, "Couldn't send your answer. Try again.");
    return false;
  }
}

export function clearPromptError(promptId: string) {
  if (promptId in promptErrors.value) {
    const { [promptId]: _dropped, ...rest } = promptErrors.value;
    promptErrors.value = rest;
  }
}

/** WS events: `prompt` adds a card, `prompt_resolved` removes it. */
export function handlePromptEvent(msg: Record<string, unknown>): boolean {
  const chatId = msg.chat_id;
  if (typeof chatId !== 'number') return false;
  if (msg.type === 'prompt') {
    bump(chatId);
    const prompt = msg.prompt as ChatPrompt | undefined;
    if (!prompt || typeof prompt.id !== 'string') {
      // Private tabs send no card body over the WS; fetch it through the lock-guarded GET.
      void loadChatPrompts(chatId);
      return true;
    }
    const current = (chatPrompts.value[chatId] ?? []).filter((item) => item.id !== prompt.id);
    setChat(chatId, [...current, prompt]);
    promptRelay.value = { ...promptRelay.value, [chatId]: true };
    return true;
  }
  if (msg.type === 'prompt_resolved') {
    bump(chatId);
    if (typeof msg.prompt_id === 'string') removePrompt(chatId, msg.prompt_id);
    return true;
  }
  return false;
}
